import { ListTodo, Lightbulb, Users, BarChart3 } from 'lucide-react';

const SkeletonLine = ({ className = "" }: { className?: string }) => (
  <div className={`skeleton-shimmer rounded ${className}`} />
);

export const SessionsListSkeleton = () => (
  <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card p-6">
    <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
    
    <div className="relative z-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
          <ListTodo className="w-5 h-5 text-primary" />
        </div>
        <div className="space-y-2">
          <SkeletonLine className="h-5 w-40" />
          <SkeletonLine className="h-3.5 w-28" />
        </div>
      </div>

      {/* Session items */}
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border border-border/50 rounded-xl p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-3">
                <div className="flex items-center gap-2">
                  <SkeletonLine className="h-5 w-48" />
                  <SkeletonLine className="h-5 w-16 rounded-full" />
                </div>
                <SkeletonLine className="h-3.5 w-24" />
              </div>
              <div className="flex items-center gap-4">
                <div className="hidden sm:block">
                  <div className="skeleton-shimmer w-[50px] h-[50px] rounded-full" />
                </div>
                <SkeletonLine className="h-9 w-24 rounded-md" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const HypothesesSkeleton = () => (
  <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card p-6">
    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
    
    <div className="relative z-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
          <Lightbulb className="w-5 h-5 text-primary" />
        </div>
        <div className="space-y-2">
          <SkeletonLine className="h-5 w-36" />
          <SkeletonLine className="h-3.5 w-32" />
        </div>
      </div>

      {/* Filter row */}
      <div className="flex items-center justify-between gap-2 mb-4 pb-4 border-b border-border/50">
        <SkeletonLine className="h-8 w-20 rounded-md" />
        <SkeletonLine className="h-8 w-24 rounded-md" />
      </div>

      {/* Hypothesis items */}
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border border-border/50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <SkeletonLine className="w-4 h-4 rounded-full shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2">
                <SkeletonLine className="h-4 w-full" />
                <SkeletonLine className="h-4 w-3/4" />
              </div>
              <SkeletonLine className="h-5 w-16 rounded-full" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const TeamActivitySkeleton = () => (
  <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card p-6">
    <div className="absolute top-0 right-0 w-40 h-40 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
    
    <div className="relative z-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
          <Users className="w-5 h-5 text-primary" />
        </div>
        <div className="space-y-2">
          <SkeletonLine className="h-5 w-32" />
          <SkeletonLine className="h-3.5 w-44" />
        </div>
      </div>

      {/* Activity items */}
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <div key={i} className="flex items-start gap-3 p-3">
            <SkeletonLine className="w-8 h-8 rounded-lg shrink-0" />
            <div className="flex-1 space-y-2">
              <SkeletonLine className="h-4 w-full" />
              <SkeletonLine className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

export const InsightsPanelSkeleton = () => (
  <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card p-6">
    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
    
    <div className="relative z-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
          <BarChart3 className="w-5 h-5 text-primary" />
        </div>
        <div className="space-y-2">
          <SkeletonLine className="h-5 w-28" />
          <SkeletonLine className="h-3.5 w-36" />
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="p-3 rounded-xl border border-border/50">
            <SkeletonLine className="h-3 w-16 mb-2" />
            <SkeletonLine className="h-6 w-10" />
          </div>
        ))}
      </div>

      {/* Chart placeholder */}
      <SkeletonLine className="h-32 w-full rounded-xl" />
    </div>
  </div>
);

export const QuickStartSkeleton = () => (
  <div className="relative overflow-hidden rounded-2xl border border-border/50 bg-card p-6">
    <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
    
    <div className="relative z-10 flex items-center justify-between gap-4">
      <div className="space-y-2">
        <SkeletonLine className="h-5 w-32" />
        <SkeletonLine className="h-4 w-48" />
      </div>
      <SkeletonLine className="h-10 w-40 rounded-md" />
    </div>
  </div>
);

export const DashboardHeaderSkeleton = () => (
  <div className="space-y-4">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <SkeletonLine className="h-8 w-48" />
        <SkeletonLine className="h-4 w-64" />
      </div>
      <SkeletonLine className="h-10 w-36 rounded-md" />
    </div>
    
    {/* Focus tip skeleton */}
    <div className="p-4 rounded-xl border border-border/50">
      <div className="flex items-center gap-3">
        <SkeletonLine className="w-8 h-8 rounded-lg" />
        <div className="flex-1 space-y-2">
          <SkeletonLine className="h-4 w-40" />
          <SkeletonLine className="h-3 w-56" />
        </div>
        <SkeletonLine className="h-8 w-28 rounded-md" />
      </div>
    </div>
  </div>
);
